<template>
  <c-m-sidebar :title="sidebarTitle" :subtitle="sidebarSubtitle">
    <template #content>
      <template v-if="activeMode === MODES.EVENT">
        <alarm :monitor_id="currentMonitor.id" is-inline @close="activeMode = null"></alarm>
      </template>
      <template v-else-if="activeMode === MODES.FORM_FILL">
        <form-fill :patient-id="patientId" :patient-monitor-id="monitorId" :readonly="true" @save="closeActiveMode" @close="activeMode = null" class="form-fill"></form-fill>
      </template>
      <template v-else-if="activeMode === MODES.SUBMISSION_VIEW && activeModeData">
        <form-fill :submission="activeModeData" :patient-id="activeModeData.patient_id" :patient-monitor-id="activeModeData.patient_monitor_id" :view-only="!(activeModeData.form.can_update && activeModeData.status === 'in_progress')" @save="closeActiveMode" @close="activeMode = null" class="form-fill"></form-fill>
      </template>
      <template v-else>
        <div class="carousel-collapse" v-if="allowedWidgets.viewCharts && $store.state.currentUser.enabled_flags.includes('patient_insights_dynamic_blocks')">
          <b-collapse animation="fade" v-model="isCarouselOpen">
            <template #trigger>
              <span>Patient Insights</span>
            </template>
            <quickview-blocks-carousel v-if="currentMonitor" :monitor="currentMonitor"></quickview-blocks-carousel>
          </b-collapse>
        </div>
        <draggable v-model="widgets" item-key="id" @change="orderChanged" filter="input" :preventOnFilter="false">
          <template #item="{element: widget}">
            <component
              v-show="fullScreenComponent.length == 0 || isExpanded(widget.componentName)"
              :class="{'widget-expanded': this.isExpanded(widget.componentName)}"
              :is="widget.componentName"
              v-bind="widget.attrs">
              <template #headerUtils>
                <b-checkbox-button
                  v-model="fullScreenComponent"
                  :native-value="widget.componentName"
                  type="is-primary"
                  class="expand-colapse-btn">
                  <i class="mdi mdi-arrow-expand"></i>
                </b-checkbox-button>
              </template>
            </component>
          </template>
       </draggable>
      </template>
    </template>
  </c-m-sidebar>
</template>

<script>
import WidgetModel from "@/models/WidgetModel"
import FormSubmissionListCard from "@/components/form_builder/FormSubmissionListCard"
import EventsCard from "@/components/EventsCard"
import CMSidebar from "@/components/safety_center/CMSidebar.vue"
import PatientNote from "@/components/PatientNote.vue"
import QuickviewBlocksCarousel from "@/components/quickview/QuickviewBlocksCarousel.vue"
import Alarm from "@/components/Alarm"
import draggable from "vuedraggable"
import to from '@/lib/to'
import { defineAsyncComponent } from "vue"
const AddPatient = defineAsyncComponent(() => import("@/views/patient/AddPatient.vue"))
import Card from "@/components/card/Card.vue"
import PatientEditCard from "@/components/PatientEditCard.vue"
import VideoReasonCard from "@/components/VideoReasonCard.vue"
import FormFill from "@/components/form_builder/FormFill"

const MODES = {
  EVENT: 'event',
  FORM_FILL: 'formFill',
  SUBMISSION_VIEW: 'submissionView'
}

export default {
  name: "VideoVisitSidebar",
  components: {
    AddPatient,
    CMSidebar,
    PatientNote,
    EventsCard,
    FormSubmissionListCard,
    QuickviewBlocksCarousel,
    Alarm,
    draggable,
    Card,
    PatientEditCard,
    VideoReasonCard,
    FormFill
  },
  data() {
    return {
      MODES,
      widgets: [],
      fullScreenComponent: [],
      isCarouselOpen: false,
      activeMode: null,
      activeModeData: null,
      fetchedMonitor: null,
      allowedWidgets: {
        viewCharts: false,
        accessEditForms: false,
        managePatientEvents: false,
      }
    }
  },
  props: {
    monitor: {
      type: Object
    },
    monitorId: {
      type: [Number, String]
    },
    patientId: {
      type: [Number, String]
    },
    eventOnly: {
      type: Boolean,
      default: false
    },
    meetingId: {
      type: Number,
    }
  },
  computed: {
    currentMonitor() {
      return this.monitor || this.fetchedMonitor
    },
    eventsModel() {
      if (!this.currentMonitor) return null
      const model = new WidgetModel('patient-events', {}, {})
      model.id = Math.random()
      model.monitor = this.currentMonitor
      model.patientId = this.currentMonitor.patient_id
      model.monitorId = this.currentMonitor.id
      return model
    },
    sidebarTitle() {
      if (this.currentMonitor) {
        return this.$helpers.mask('patient_name', this.currentMonitor.patient.display_name)
      } else {
        return 'Video Visit'
      }
    },
    sidebarSubtitle() {
      if (this.currentMonitor) {
        return this.$helpers.mask('mrn', this.currentMonitor.patient.mrn) + ' / ' + this.currentMonitor.room.name
      } else {
        return ''
      }
    }
  },
  methods: {
    async fetchMonitor() {
      if (this.monitorId && this.$can('View', 'PatientMonitor')) {
        const [err, response] = await to(this.$http.get(`/patient_monitors/${this.monitorId}`, { skipLoading: true, skipDivisions: true }))
        if (!err) {
          this.fetchedMonitor = response.data
        }
      }
    },

    async fetchPagePreference() {
      const [err, response] = await to(this.$http.get('/page_preferences/for_current_user/video_visit', { skipLoading: true }))
      if (!err && response.data) {
        const pagePreference = response.data
        if (pagePreference.settings && pagePreference.settings.additional_data) {
          this.allowedWidgets = pagePreference.settings.additional_data
        }
      }
      this.buildWidgetsArray()
    },

    buildWidgetsArray() {
      const allWidgets = []

      allWidgets.push({
        componentName: 'patientEditCard',
        attrs: {
          hasSettings: false,
          hasSubtitle: false,
          title: 'Patient',
          autoHeight: true,
          patientId: this.patientId,
          monitorId: this.monitorId,
        }
      })

      if (this.meetingId) {
        allWidgets.push({
          componentName: 'videoReasonCard',
          attrs: {
            meetingId: this.meetingId,
            cardAttrs: { autoHeight: true }
          }
        })
      }

      if (this.allowedWidgets.managePatientEvents && this.eventsModel) {
        allWidgets.push({
          componentName: 'eventsCard',
          attrs: {
            widgetModel: this.eventsModel,
            cardAttrs: {
              autoHeight: true,
              title: 'Patient Events',
              hasSubtitle: false,
              maintainAspectRatio: false
            },
            handleNewEvent: true,
            onNewEventOpen: this.newEventOpen
          }
        })
      }

      if (this.allowedWidgets.accessEditForms) {
        allWidgets.push({
          componentName: 'formSubmissionListCard',
          attrs: {
            patientMonitorId: this.monitorId,
            patientId: this.patientId,
            cardAttrs: { autoHeight: true },
            handleFillForm: true,
            onFillFormOpen: this.fillFormOpen,
            handleViewSubmission: true,
            onViewSubmission: this.viewSubmissionOpen
          }
        })
      }

      this.widgets = allWidgets
      this.orderWidgets()
    },

    orderWidgets() {
      const pref = this.$store.getters['UserPreferencesStore/getPreference']('videoVisitSidebar')
      const order = pref?.widget_order
      if (order && Array.isArray(order)) {
        this.widgets = this.widgets.sort((a, b) => {
          if (order.indexOf(a['componentName']) > order.indexOf(b['componentName'])) {
            return 1
          } else {
            return -1
          }
        })
      }
    },

    async orderChanged() {
      const widgetsOrder = this.widgets.map(x => x.componentName)
      await this.$store.dispatch('UserPreferencesStore/set', {
        key: 'videoVisitSidebar',
        value: { widget_order: widgetsOrder }
      })
    },

    isExpanded(componentName) {
      return this.fullScreenComponent[0] == componentName
    },

    newEventOpen() {
      this.activeMode = MODES.EVENT
    },

    fillFormOpen() {
      this.activeMode = MODES.FORM_FILL
    },

    viewSubmissionOpen(submission) {
      this.activeMode = MODES.SUBMISSION_VIEW
      this.activeModeData = submission
    },

    closeActiveMode() {
      this.activeMode = null
      this.activeModeData = null
    }
  },

  async beforeMount() {
    if (!this.monitor && this.monitorId) {
      await this.fetchMonitor()
    }
    await this.fetchPagePreference()
  },

  watch: {
    monitor() {
      this.buildWidgetsArray()
      // $http.put('/meetings/729/video_reason', { video_reason_id: 3 })
    },
    async monitorId() {
      if (!this.monitor && this.monitorId) {
        await this.fetchMonitor()
        await this.fetchPagePreference()
      }
    },
    currentMonitor() {
      this.buildWidgetsArray()
    },
    eventOnly: {
      handler(value) {
        if (value) {
          this.activeMode = MODES.EVENT
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped lang="scss">
:deep() {
  .quickview__widgets {
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
    > div > div {
      margin-bottom: 30px;
    }
    :deep(.patient-events, .form-submission-card .monitoring-card__content > div) {
      max-height: 200px;
      overflow-y: auto !important;
    }
  }
  .carousel-collapse {
    margin-bottom: 30px;
    .collapse-trigger {
      display: block;
      background: #2d7bff52;
      padding: 15px;
      font-size: 13px;
      font-weight: 500;
      border-radius: 4px;
      letter-spacing: 0.4px;
    }
    .collapse-content {
      margin-top: -5px;
    }
    > div {
      margin-bottom: 0 !important;
    }
  }
}

.widget-expanded {
  :deep(.monitoring-card__content > div) {
    max-height: 100%;
    overflow-y: hidden !important;
  }
}
.form-fill {
  :deep(> div) {
    margin-bottom: 0 !important;
  }
  :deep(.form-modal) {
    width: auto;
    max-height: none;
  }
}
</style>